import { useCallback, useEffect, useState } from "react";
import {
  clinicalApi,
  type DailySummary,
  type RiskQueueRow,
} from "../../lib/api/clinical";
import { LEVEL_LABELS, WORKFLOW_LABELS, levelClass } from "./format";

interface Props {
  refreshKey: number;
  onOpenPatient: (patientId: string) => void;
}

export function Dashboard({ refreshKey, onOpenPatient }: Props) {
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [queue, setQueue] = useState<RiskQueueRow[]>([]);
  const [actionableOnly, setActionableOnly] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const [summaryData, queueData] = await Promise.all([
      clinicalApi.dailySummary(),
      clinicalApi.riskQueue(undefined, actionableOnly),
    ]);
    setSummary(summaryData);
    setQueue(queueData);
  }, [actionableOnly]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load().catch(() => {
      setError("加载工作台数据失败");
    });
  }, [load, refreshKey]);

  const cards: { label: string; value: number; tone: string }[] = summary
    ? [
        { label: "立即处理", value: summary.immediate_count, tone: "tier-immediate" },
        { label: "今日评估", value: summary.today_count, tone: "tier-today" },
        { label: "在办工单", value: summary.open_workflows, tone: "" },
        { label: "超时未处理", value: summary.overdue_workflows, tone: "tier-immediate" },
        { label: "在管导管", value: summary.active_catheters, tone: "" },
        { label: "留置超14天", value: summary.catheters_over_14_days, tone: "tier-today" },
        { label: "待复核", value: summary.pending_reviews, tone: "" },
        { label: "确认CAUTI", value: summary.confirmed_cauti, tone: "" },
      ]
    : [];

  return (
    <div className="view">
      {error ? <p role="alert">{error}</p> : null}
      <section className="kpi-grid">
        {cards.map((card) => (
          <div key={card.label} className={`kpi-card ${card.tone}`}>
            <span className="kpi-value">{card.value}</span>
            <span className="kpi-label">{card.label}</span>
          </div>
        ))}
      </section>

      <section>
        <div className="section-head">
          <h2>今日重点患者</h2>
          <label className="toggle">
            <input
              type="checkbox"
              checked={actionableOnly}
              onChange={(event) => {
                setActionableOnly(event.currentTarget.checked);
              }}
            />
            仅看立即处理
          </label>
        </div>
        {queue.length === 0 ? (
          <p>暂无重点患者。可在「风险预测」导入或录入数据后生成。</p>
        ) : (
          <table className="queue-table">
            <thead>
              <tr>
                <th>风险</th>
                <th>指数</th>
                <th>脱敏编号</th>
                <th>病区/床位</th>
                <th>导管天数</th>
                <th>主要因素</th>
                <th>状态</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {queue.map((row) => (
                <tr key={row.assessment_id} className={row.overdue ? "overdue" : ""}>
                  <td>
                    <span className={`pill ${levelClass(row.level)}`}>
                      {row.tier_label || LEVEL_LABELS[row.level]}
                    </span>
                  </td>
                  <td>{row.risk_index}</td>
                  <td>{row.external_id}</td>
                  <td>
                    {row.ward ?? "—"} / {row.bed ?? "—"}
                  </td>
                  <td>{row.catheter_days ?? "—"}</td>
                  <td>{row.factors.slice(0, 3).join("、")}</td>
                  <td>
                    {row.workflow_state ? WORKFLOW_LABELS[row.workflow_state] : "—"}
                    {row.overdue ? <span className="overdue-flag">超时</span> : null}
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => {
                        onOpenPatient(row.patient_id);
                      }}
                    >
                      处置
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
